import { HTMLAttributes, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { down, up } from 'styled-breakpoints';
import styled from 'styled-components';
import { ColorPaletteItem } from '@lgg/isomorphic';
import {
  MetricSummaryAgentPerformanceActivityFilter,
  MetricSummaryAgentPerformanceMetric,
  MetricTrendChangeIndicator,
  Query,
  QueryMetricSummaryAgentPerformanceArgs,
  User,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { AssigneeLabel } from 'src/components/general/display/assignee-label';
import { FlexRow } from 'src/components/layout/flex-row';
import { DashboardFiltersContext } from 'src/components/pages/dashboard/components/filters/dashboard-filters-context';
import {
  Card,
  CardBaseContent,
  ChipFilters,
  CompareBadge,
  DashboardCard,
  dashboardTableBodyOVerlayStyles,
  DashboardTableEmptyValue,
  useCardValueHelpers,
} from 'src/components/pages/dashboard/components/shared';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

type MetricValue = {
  total: number;
  previousTotal?: number;
  trendChangePercentage?: number;
  trendChangeIndicator?: MetricTrendChangeIndicator;
};

type TableRow = {
  user: User;
  contactsCalled: MetricValue;
  contactsCallsAnswered: MetricValue;
  contactsReachedViaMessage: MetricValue;
  scheduledAppointments: MetricValue;
  completedAppointments: MetricValue;
  createdTasks: MetricValue;
  completedTasks: MetricValue;
  customersWon: MetricValue;
};

const QUERY_PAGE_SIZE = 200;

export const GET_METRIC_SUMMARY_AGENT_PERFORMANCE_QUERY = gql`
  query GetMetricSummaryAgentPerformance(
    $institutionId: Int!
    $period: MetricPeriodWhereInput!
    $comparePeriod: MetricComparePeriodInput
    $after: String
    $where: MetricSummaryAgentPerformanceWhereInput
  ) {
    metricSummaryAgentPerformance(
      institutionId: $institutionId
      period: $period
      comparePeriod: $comparePeriod
      first: ${QUERY_PAGE_SIZE}
      after: $after
      where: $where
    ) {
      edges {
        node {
          lastUpdatedAt
          user {
            id
            fullName
            avatar {
              url(transform: { width: 180, height: 180 })
              color
              initials
            }
            role {
              id
              name
            }
          }
          metrics {
            total
            metric
            previousPeriod {
              total
              trendChangeIndicator
              trendChangePercentage
            }
          }
        }
      }
    }
  }
`;

const StyledCard = styled(Card)`
  width: 100%;
`;

const ColumnTitle = styled.span<{ $bgColor: ColorPaletteItem }>`
  background-color: ${({ theme, $bgColor }) => theme.colors[$bgColor]};
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 11px;
  font-style: normal;
  letter-spacing: -0.11px;
  line-height: 11px;
  height: 33px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 10px;
`;

const TwoRowsColumnTitleFirst = styled(ColumnTitle)`
  height: 58px;
  margin-left: 0;
  text-align: left;

  > div {
    width: 100%;
    margin-left: 10px;
  }
`;

const TwoRowsColumnTitleLast = styled(TwoRowsColumnTitleFirst)`
  margin-left: 1px;
  margin-right: 0;
`;

type TitleProps = { $bgColor: ColorPaletteItem };

const ColumnGroupTitle = styled.span<TitleProps>`
  background-color: ${({ theme, $bgColor }) => theme.colors[$bgColor]};
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 10px;
  font-style: normal;
  letter-spacing: 0.3px;
  line-height: 13px;
  text-align: center;
  text-transform: uppercase;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ColumnValueTotal = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  font-style: normal;
  letter-spacing: -0.12px;
  line-height: 15px;
  text-align: right;
`;

const ColumnValueCell = styled(FlexRow)`
  align-items: center;
  display: flex;
  justify-content: flex-end;
`;

const ColumnValueDiff = styled.div`
  margin-left: 8px;
  min-width: 44px;
  display: flex;
  justify-content: flex-end;
`;

const StyledAssigneeLabel = styled(AssigneeLabel)`
  max-width: 130px;

  ${up('md')} {
    max-width: 270px;
  }

  .assignee-label-name {
    color: ${({ theme }) => theme.colors.smalt};
    font-family: ${({ theme }) => theme.font.medium};
    font-size: 12px;
    font-style: normal;
    line-height: 15px;
  }
`;

const StyledTable = styled(Table<TableRow>)`
  .ant-table-cell {
    padding: 0;
  }

  .ant-table-cell:not(.agent-column) {
    border-left: 1px solid ${({ theme }) => theme.colors.porcelain2};
  }

  .ant-table-thead {
    border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain2};
  }

  .ant-table-tbody > tr > td {
    border-bottom: none;
    height: 50px;
    padding: 0 20px;

    &:first-child {
      padding: 0 10px 0 20px;

      ${up('md')} {
        padding: 0 20px;
      }
    }
  }

  .ant-table-thead > tr > th {
    border-bottom: none;
    background-color: white;
  }

  // Border styles

  th.agent-column > div {
    padding: 1px 1px 1px 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain2};
  }

  th.customer-won-column > div {
    padding: 1px 0 1px 1px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain2};
  }

  th.group > div {
    padding: 1px 1px 0 1px;
  }

  th.grouped-first > div {
    padding-left: 1px;
    padding-bottom: 1px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain2};
  }

  th.grouped-middle > div {
    padding-bottom: 1px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain2};
  }

  th.grouped-last > div {
    padding-right: 1px;
    padding-bottom: 1px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain2};
  }

  .ant-table-cell.agent-column {
    max-width: 160px;
    width: 160px;
    z-index: 1;

    ${up('md')} {
      width: 270px;
    }
  }

  ${down('md')} {
    .ant-table-cell.agent-column {
      ${StyledAssigneeLabel} {
        .assignee-label-name {
          margin-left: 10px;
          opacity: 1;
          transition: opacity 0.5s ease-in-out, width 0.5s, margin-left 1s;
          width: 130px;
        }
      }
    }

    .ant-table-ping-left {
      .ant-table-tbody > tr > td:first-child {
        padding: 0 20px;
      }

      .ant-table-cell.agent-column {
        width: 70px;

        ${StyledAssigneeLabel} {
          .assignee-label-name {
            margin-left: 0;
            opacity: 0;
            width: 0;
          }
        }
      }
    }
  }

  .ant-table .ant-table-tbody {
    tr:last-child {
      td {
        height: 70px;
        padding-bottom: 20px;
      }
    }

    ${dashboardTableBodyOVerlayStyles};
  }
`;

const StyledCardContent = styled(Card.Content)`
  border-bottom-right-radius: 11px;
  border-bottom-left-radius: 11px;
  overflow: hidden;
`;

const CardContentContainer = styled(CardBaseContent)`
  height: 368px;
`;

const StyledCompareBadge = styled(CompareBadge)`
  justify-content: center;
  margin-left: 0;
`;

const StyledChipFilters = styled(ChipFilters)`
  padding: 10px 0 10px 20px;
  border-bottom: solid 1px ${({ theme }) => theme.colors.koala};

  ${up('md')} {
    border-bottom: none;
    padding: 0 0 0 20px;
  }
` as typeof ChipFilters;

export const AgentPerformanceCard = () => {
  const currentInstitution = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();
  const { t } = useTranslation(['common', 'dashboard']);
  const { metricPeriodWhereInput, metricComparePeriodWhereInput } = useContext(
    DashboardFiltersContext,
  );
  const { getNumericValueAbbreviation } = useCardValueHelpers();
  const [selectedFilter, setSelectedFilter] =
    useState<MetricSummaryAgentPerformanceActivityFilter>('WITH_ACTIVITY');
  const breakpointUpMd = useBreakpoint(up('md'));

  const summaryAgentPerformanceQuery = useQuery<
    Pick<Query, 'metricSummaryAgentPerformance'>,
    Partial<QueryMetricSummaryAgentPerformanceArgs>
  >(GET_METRIC_SUMMARY_AGENT_PERFORMANCE_QUERY, {
    variables: {
      institutionId: currentInstitution.id,
      period: metricPeriodWhereInput,
      comparePeriod: metricComparePeriodWhereInput,
      where: { activityFilter: selectedFilter },
    },
    onError: handleGraphQLError,
  });

  const { data, loading } = summaryAgentPerformanceQuery;

  const metricSummaryAgentPerformance = data?.metricSummaryAgentPerformance;

  const { edges: agentPerformanceEdges = [] } = metricSummaryAgentPerformance ?? {};
  const updatedAt = agentPerformanceEdges[0]?.node.lastUpdatedAt;
  const agentNodes = agentPerformanceEdges.map((edge) => edge.node);

  const agentsWithMetric: TableRow[] = agentNodes.map((node) => {
    const metricsMap: Record<
      MetricSummaryAgentPerformanceMetric,
      {
        total: number;
        previousTotal?: number;
        trendChangePercentage?: number;
        trendChangeIndicator?: MetricTrendChangeIndicator;
      }
    > = {} as Record<
      MetricSummaryAgentPerformanceMetric,
      {
        total: number;
        previousTotal?: number;
        trendChangePercentage?: number;
        trendChangeIndicator?: MetricTrendChangeIndicator;
      }
    >;

    for (const metricNode of node.metrics) {
      const { total: metricTotal = null, previousPeriod, metric } = metricNode;

      if (metricTotal !== null) {
        metricsMap[metric] = {
          total: metricTotal,
          previousTotal: previousPeriod?.total,
          trendChangePercentage: previousPeriod?.trendChangePercentage,
          trendChangeIndicator: previousPeriod?.trendChangeIndicator,
        };
      }
    }

    return {
      user: node.user,
      contactsCalled: metricsMap['CONTACTS_CALLED'],
      contactsCallsAnswered: metricsMap['CONTACTS_CALLS_ANSWERED'],
      contactsReachedViaMessage: metricsMap['CONTACTS_REACHED_VIA_MESSAGE'],
      scheduledAppointments: metricsMap['SCHEDULED_APPOINTMENTS'],
      completedAppointments: metricsMap['COMPLETED_APPOINTMENTS'],
      createdTasks: metricsMap['CREATED_TASKS'],
      completedTasks: metricsMap['COMPLETED_TASKS'],
      customersWon: metricsMap['CUSTOMERS_WON'],
    };
  });

  const renderValue = ({ value, testId }: { value: MetricValue; testId: string }) => {
    return (
      <ColumnValueCell>
        <ColumnValueTotal data-lgg-id={`cell-${testId}-total`}>
          {getNumericValueAbbreviation(value.total)}
        </ColumnValueTotal>
        <ColumnValueDiff data-lgg-id={`cell-${testId}-diff`}>
          {value.previousTotal !== undefined ? (
            <StyledCompareBadge
              trendChangeIndicator={value.trendChangeIndicator}
              trendChangePercentage={value.trendChangePercentage}
              testId="cell"
              variant="compact"
              fromValue={value.previousTotal.toLocaleString()}
              toValue={value.total.toLocaleString()}
              forceEmptyState={value.previousTotal === 0}
            />
          ) : (
            <DashboardTableEmptyValue />
          )}
        </ColumnValueDiff>
      </ColumnValueCell>
    );
  };

  const renderColumnGroupTitle = ({
    $bgColor,
    text,
    Component,
  }: {
    $bgColor: ColorPaletteItem;
    text: string;
    Component: React.ComponentType<TitleProps>;
  }) => {
    return (
      <div className="title-wrapper">
        <Component $bgColor={$bgColor}>{text}</Component>
      </div>
    );
  };

  const columns: ColumnsType<TableRow> = [
    {
      key: 'agents',
      fixed: 'left',
      className: 'agent-column',
      title: (
        <div className="title-wrapper">
          <TwoRowsColumnTitleFirst $bgColor="alabaster">
            <div>{t('dashboard:cards.agentPerformance.columns.users')}</div>
          </TwoRowsColumnTitleFirst>
        </div>
      ),
      render: (_, record) => <StyledAssigneeLabel assignee={record.user} />,
    },
    {
      key: 'interactions',
      title: renderColumnGroupTitle({
        $bgColor: 'topaz20',
        text: t('dashboard:cards.agentPerformance.columns.interactions'),
        Component: ColumnGroupTitle,
      }),
      width: 357,
      className: 'group',
      children: [
        {
          key: 'contactsCalled',
          width: 119,
          className: 'grouped-first',
          title: renderColumnGroupTitle({
            $bgColor: 'topaz10',
            text: t('dashboard:cards.agentPerformance.columns.contactsCalled'),
            Component: ColumnTitle,
          }),
          render: (_, record) =>
            renderValue({ value: record.contactsCalled, testId: 'contacts-called' }),
        },
        {
          key: 'contactsCallsAnswered',
          width: 119,
          className: 'grouped-middle',
          title: renderColumnGroupTitle({
            $bgColor: 'topaz10',
            text: t('dashboard:cards.agentPerformance.columns.contactsCallsAnswered'),
            Component: ColumnTitle,
          }),
          render: (_, record) =>
            renderValue({
              value: record.contactsCallsAnswered,
              testId: 'contacts-calls-answered',
            }),
        },
        {
          key: 'contactsReachedViaMessage',
          width: 119,
          className: 'grouped-last',
          title: renderColumnGroupTitle({
            $bgColor: 'topaz10',
            text: t('dashboard:cards.agentPerformance.columns.contactsReachedViaMessage'),
            Component: ColumnTitle,
          }),
          render: (_, record) =>
            renderValue({
              value: record.contactsReachedViaMessage,
              testId: 'contacts-reached-via-message',
            }),
        },
      ],
    },
    {
      key: 'appointments',
      width: 238,
      title: renderColumnGroupTitle({
        $bgColor: 'periWinkle20',
        text: t('dashboard:cards.agentPerformance.columns.appointments'),
        Component: ColumnGroupTitle,
      }),
      className: 'group',
      children: [
        {
          key: 'scheduledAppointments',
          width: '119px',
          className: 'grouped-first',
          title: renderColumnGroupTitle({
            $bgColor: 'periWinkle10',
            text: t('dashboard:cards.agentPerformance.columns.scheduledAppointments'),
            Component: ColumnTitle,
          }),
          render: (_, record) =>
            renderValue({
              value: record.scheduledAppointments,
              testId: 'scheduled-appointments',
            }),
        },
        {
          key: 'completedAppointments',
          width: '119px',
          className: 'grouped-last',
          title: renderColumnGroupTitle({
            $bgColor: 'periWinkle10',
            text: t('dashboard:cards.agentPerformance.columns.completedAppointments'),
            Component: ColumnTitle,
          }),
          render: (_, record) =>
            renderValue({
              value: record.completedAppointments,
              testId: 'completed-appointments',
            }),
        },
      ],
    },
    {
      title: renderColumnGroupTitle({
        $bgColor: 'secondaryMint20',
        text: t('dashboard:cards.agentPerformance.columns.tasks'),
        Component: ColumnGroupTitle,
      }),
      key: 'tasks',
      className: 'group',
      width: 238,
      children: [
        {
          key: 'createdTasks',
          width: '119px',
          className: 'grouped-first',
          title: renderColumnGroupTitle({
            $bgColor: 'secondaryMint10',
            text: t('dashboard:cards.agentPerformance.columns.createdTasks'),
            Component: ColumnTitle,
          }),
          render: (_, record) =>
            renderValue({ value: record.createdTasks, testId: 'created-tasks' }),
        },
        {
          key: 'completedTasks',
          width: '119px',
          className: 'grouped-last',
          title: renderColumnGroupTitle({
            $bgColor: 'secondaryMint10',
            text: t('dashboard:cards.agentPerformance.columns.completedTasks'),
            Component: ColumnTitle,
          }),
          render: (_, record) =>
            renderValue({ value: record.completedTasks, testId: 'completed-tasks' }),
        },
      ],
    },
    {
      key: 'closeWon',
      width: '118px',
      className: 'customer-won-column',
      title: renderColumnGroupTitle({
        $bgColor: 'alabaster',
        text: t('dashboard:cards.agentPerformance.columns.customerWon'),
        Component: TwoRowsColumnTitleLast,
      }),
      render: (_, record) =>
        renderValue({ value: record.customersWon, testId: 'customer-won' }),
    },
  ];

  const hasRecords = agentsWithMetric.length > 0;

  const cardTitle = t('dashboard:cards.agentPerformance.title');

  const filterOptions: {
    value: MetricSummaryAgentPerformanceActivityFilter;
    label: string;
    testId: string;
  }[] = useMemo(
    () => [
      {
        value: 'WITH_ACTIVITY',
        label: t('dashboard:cards.agentPerformance.filters.withActivity'),
        testId: 'dashboard-agent-performance-filter-with-activity',
      },
      {
        value: 'ALL',
        label: t('common:all'),
        testId: 'dashboard-agent-performance-filter-upcoming',
      },
    ],
    [t],
  );

  const filtersChips = (
    <StyledChipFilters
      options={filterOptions}
      selectedOption={selectedFilter}
      onChanged={setSelectedFilter}
    />
  );

  return (
    <StyledCard loading={loading} data-lgg-id="dashboard-agent-performance-card">
      <Card.Header
        title={
          <FlexRow>
            {cardTitle}
            {breakpointUpMd && filtersChips}
          </FlexRow>
        }
        updatedAt={updatedAt}
        cardInfoI18nKey="dashboard:cards.agentPerformance.tooltipDescription"
        cardTextTitle={cardTitle}
      />
      {!breakpointUpMd && filtersChips}
      <StyledCardContent>
        <CardContentContainer $isEmpty={!hasRecords}>
          {hasRecords ? (
            <StyledTable
              pagination={false}
              rowKey={(record) => record.user.id}
              dataSource={agentsWithMetric}
              columns={columns}
              onRow={(record) => {
                return {
                  'data-lgg-id': `row-user-${record.user.id}`,
                } as HTMLAttributes<HTMLElement>;
              }}
              scroll={{ x: 'max-content', y: 310 }}
            />
          ) : (
            <DashboardCard.EmptyState
              icon="board"
              title={t('dashboard:cards.agentPerformance.emptyState.title')}
              subTitle={t('dashboard:cards.agentPerformance.emptyState.subTitle')}
              testId="agent-performance-empty"
              iconVariant="large"
            />
          )}
        </CardContentContainer>
      </StyledCardContent>
    </StyledCard>
  );
};
